'use client';

/**
 * Docs Explorer Panel
 *
 * @component_role Documentation Q&A Interface
 * @business_rule Provides AI-powered documentation search.
 *   Maintains conversation context for multi-turn follow-up questions.
 *   Answers displayed in Canvas widget with sources.
 * @user_outcome Ask questions about the Klyde platform in natural language.
 * @integration_point klyde_edge /docs/ask - documentation Q&A
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain docs
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Search, Loader2, Clock, BookOpen, X } from 'lucide-react';
import { useDocs } from '@/lib/api/hooks';
import { useDocsStore } from '@/lib/stores/docs-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { cn } from '@/lib/utils/cn';
import type { DocsResponse } from '@/lib/api/types';

interface ConversationTurn {
  id: string;
  query: string;
  timestamp: Date;
  shortAnswer: string;
  confidence: string;
}

export function DocsExplorer() {
  const [query, setQuery] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [history, setHistory] = useState<ConversationTurn[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const askStrategy = useCapabilityStore((s) => s.strategyName('docs', 'ask'));
  const docs = useDocs(askStrategy!);
  const { response: currentResponse, setResponse, setLoading, clear: clearDocsStore } = useDocsStore();

  // Clear stale data from a previous session when entering docs mode.
  // (No doc pre-upload — /docs/ask grounds on the GCS-fetched retrieval
  //  frontier inline; there's no Gemini File API cache to warm.)
  useEffect(() => {
    clearDocsStore();
  }, [clearDocsStore]);

  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) return;

    setLoading(true);

    docs.mutate(
      {
        query: searchQuery.trim(),
        conversation_id: conversationId,
      },
      {
        onSuccess: (data: DocsResponse) => {
          // Update conversation ID for multi-turn
          setConversationId(data.conversation_id);

          // Store response in shared store
          setResponse(data);

          // Add to history
          setHistory((prev) => [
            {
              id: `${data.conversation_id}-${data.turn}`,
              query: searchQuery,
              timestamp: new Date(),
              shortAnswer: data.short_answer,
              confidence: data.confidence,
            },
            ...prev,
          ]);

          // Clear input
          setQuery('');
        },
      }
    );
  }, [conversationId, docs, setLoading, setResponse]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(query);
  };

  const handleHistoryClick = (turn: ConversationTurn) => {
    setQuery(turn.query);
    inputRef.current?.focus();
  };

  const handleNewConversation = () => {
    setConversationId(null);
    setHistory([]);
    clearDocsStore();
    setQuery('');
    inputRef.current?.focus();
  };

  const confidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'text-green-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-orange-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Input Area */}
      <div className="p-3 border-b border-border">
        <form onSubmit={handleSubmit}>
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask about Klyde..."
              className={cn(
                'w-full pl-9 pr-12 py-2 text-sm rounded-md',
                'bg-muted border border-border',
                'focus:outline-none focus:ring-1 focus:ring-primary',
                'placeholder:text-muted-foreground'
              )}
              disabled={docs.isPending}
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />

            {/* Submit button */}
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {docs.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary" />
              ) : (
                <button
                  type="submit"
                  disabled={!query.trim()}
                  className="p-1 hover:bg-accent rounded disabled:opacity-50"
                >
                  <BookOpen className="w-4 h-4 text-primary" />
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Conversation context indicator */}
        {conversationId && (
          <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
            <span>Turn {history.length} of conversation</span>
            <button
              onClick={handleNewConversation}
              className="flex items-center gap-1 hover:text-foreground"
            >
              <X className="w-3 h-3" />
              New question
            </button>
          </div>
        )}
      </div>

      {/* Conversation History */}
      <div className="flex-1 overflow-auto">
        {history.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            <BookOpen className="w-8 h-8 mx-auto mb-2 text-primary/50" />
            <p className="font-medium mb-1">Documentation Q&A</p>
            <p className="text-xs">
              Ask questions about the Klyde platform, sync, discovery, and more.
            </p>
          </div>
        ) : (
          <div className="py-1">
            {history.map((turn) => (
              <button
                key={turn.id}
                onClick={() => handleHistoryClick(turn)}
                className={cn(
                  'w-full flex items-start gap-2 px-3 py-2 text-left',
                  'hover:bg-accent/50 transition-colors text-sm'
                )}
              >
                <Clock className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{turn.query}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {turn.shortAnswer}
                  </p>
                </div>
                <span className={cn('text-xs', confidenceColor(turn.confidence))}>
                  {turn.confidence}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Error display */}
      {docs.isError && (
        <div className="p-3 border-t border-border text-sm text-destructive">
          Error: {docs.error?.message || 'Unknown error'}
        </div>
      )}
    </div>
  );
}
